(ns frontend.ux-overhaul.listing.kyc.event
  (:require
   [common.localization :as loc]
   [frontend.conn :as conn]
   [frontend.logging :as logging]
   [frontend.ux-overhaul.listing.generic.model :as generic-listing-model]
   [frontend.ux-overhaul.listing.generic.selection :as selection-helpers]
   [common.schema.listing-definition :as listing-definition]
   [fx.ajax :as ajax]
   [frontend.ux-overhaul.listing.generic.event :as generic-event]
   [frontend.view.entity :as entity]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.view.modals.events :as modals]
   [frontend.utils :as fu]
   [frontend.subscription :as ws-sub]
   [frontend.ux-overhaul.listing.kyc.model :as model]
   [promesa.core :as p]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [ui.completion.event :as completion-event]
   [ui.fx.toast :as toast]
   [ui.view.kanban-opensearch.model :as kanban-model]
   [ui.editors.simple-filters.model :as simple-filters-model]
   [ui.view.grid-opensearch.model :as grid-model]
   [ui.view.grid-opensearch.event :as grid-event]))

(rf/reg-event-fx
 ::load-profile-ok
 (fn [{db :db} [_ resp]]
   (let [entity-updates (:entities resp)
         entities (merge (get-in db model/entities-full-path)
                         (->> entity-updates
                              (map (fn [entity]
                                     [(:db/id entity) entity]))
                              (into {})))]
     {:db (-> db
              (assoc-in model/entities-full-path entities))})))

(rf/reg-event-fx
 ::load-profile-failed
 (fn [_ [_ err]]
   (js/console.warn "Profile failed " err)))

(rf/reg-event-fx
 ::load-profile
 (fn [{db :db} [_]]
   (let [profile-id (get-in db model/selected-profile-id-full-path)]
     {:fx [[::ws-sub/open
            {:key                                    (keyword (str "screening-" profile-id))
             :type                                   :entity-updates
             :opts                                   {:entity-id profile-id}
             :on-update-event                        [::load-profile-ok]
             :on-error-event                         [::load-profile-failed]
             :close-previous-subscription-if-exists? true}]]})))

(rf/reg-event-fx
 ::set-selected-screening-profile
 (fn [{db :db} [_ screening-profile-id]]
   {:db (-> db
            (assoc-in model/selected-profile-id-full-path screening-profile-id))
    :fx [[:dispatch [::load-profile]]]}))

(rf/reg-event-db
 ::close-profile-modal
 (fn [db _]
   (-> db
       (potpuri/dissoc-in model/selected-profile-id-full-path)
       (potpuri/dissoc-in model/entities-full-path))))

(rf/reg-event-db
 ::open-bulk-edit
 (fn [db _]
   (assoc-in db model/bulk-edit-open?-full-path true)))

(rf/reg-event-fx
 ::close-bulk-edit
 (fn [{:keys [db]} [_ {:keys [listing-id]}]]
   {:db (assoc-in db model/bulk-edit-open?-full-path false)
    :fx [[:dispatch [::grid-event/trigger-query-search
                     (str listing-id "-grid")]]]}))

(rf/reg-event-fx
 ::export-grid
 (fn [_ [_ {:keys [listing-id]}]]
   {:fx [[:dispatch [::run-export-grid {:listing-id listing-id
                                        :include-associated-persons? false}]]]}))

(rf/reg-event-fx
 ::export-grid-with-contacts
 (fn [_ [_ {:keys [listing-id]}]]
   {:fx [[:dispatch [::run-export-grid {:listing-id listing-id
                                        :include-associated-persons? true}]]]}))

(rf/reg-event-fx
 ::run-export-grid
 (fn [{:keys [db]} [_ {:keys [listing-id include-associated-persons?]}]]
   (let [runtime (get-in db (generic-listing-model/listing-path listing-id))
         view-mode (get-in runtime [:view :mode])
         grid-id (str listing-id "-grid")
         kanban-id (str listing-id "-kanban")
         grid-state (get-in db (grid-model/grid-path grid-id))
         kanban-compiled-fields (->> (get-in db (conj (kanban-model/compiled-config-path kanban-id) :fields) [])
                                     (keep (fn [field-path]
                                             (cond
                                               (vector? field-path) field-path
                                               (keyword? field-path) [field-path]
                                               (sequential? field-path) (vec field-path)
                                               :else nil)))
                                     vec)
         filter-model (get grid-state grid-model/filters-key)
         config (get grid-state grid-model/config-key)
         ;; The toolbar's query text and filters belong to the LISTING, not to
         ;; one view mode - see the matching note in
         ;; frontend.ux-overhaul.listing.pipeline-entity. Reading the grid's
         ;; copy meant a kanban export ignored them entirely, and this listing
         ;; opens in kanban by default, so its export was unfiltered unless the
         ;; user had switched to grid first. `filter-model` above stays
         ;; grid-scoped: it is the ag-grid column filter.
         filters-editor-id (or (get-in runtime [:filters :editor-id])
                               (str listing-id "-filters"))
         external-filters-by-field-path (get-in db (conj (simple-filters-model/filters-path filters-editor-id)
                                                         :by-field-path))
         external-filter-context (get-in db (generic-listing-model/context-path listing-id))
         external-query-text (get-in db (conj (generic-listing-model/query-path listing-id) :text))
         {:keys [columns hidden-columns]} config
         grid-visible-columns (->> columns
                                   (remove (set hidden-columns))
                                   vec)
         card-selected-fields (->> (get-in runtime [:view-config :card :by-view-mode view-mode :body-fields])
                                   (keep (fn [field-path]
                                           (cond
                                             (vector? field-path) field-path
                                             (keyword? field-path) [field-path]
                                             (sequential? field-path) (vec field-path)
                                             :else nil)))
                                   vec)
         default-card-fields (->> (or (:body-fields (listing-definition/card-config :entity.type/kyc-profile view-mode)) [])
                                  (keep (fn [field-path]
                                          (cond
                                            (vector? field-path) field-path
                                            (keyword? field-path) [field-path]
                                            (sequential? field-path) (vec field-path)
                                            :else nil)))
                                  vec)
         minimal-kanban-fallback-fields [[:kyc-profile/name]
                                         [:kyc-profile/status]
                                         [:common/updated-on]]
         effective-card-fields (if (seq card-selected-fields)
                                 card-selected-fields
                                 default-card-fields)
         export-fields (if (= view-mode :kanban)
                         (if (seq kanban-compiled-fields)
                           kanban-compiled-fields
                           (if (seq effective-card-fields)
                             effective-card-fields
                             minimal-kanban-fallback-fields))
                         grid-visible-columns)
         query-context (assoc (or external-filter-context {})
                              :entity-type
                              :entity.type/kyc-profile
                              :query-field-paths
                              export-fields)
         request-body {:entity-type :entity.type/kyc-profile
                       :fields export-fields
                       :opensearch-queries (concat []
                                                   (listing-definition/toolbar-filter-clauses external-filters-by-field-path
                                                                                              external-filter-context)
                                                   (listing-definition/toolbar-query-clauses external-query-text
                                                                                             query-context)
                                                   (when filter-model
                                                     (grid-event/filter-model->os-clauses filter-model columns)))
                       :pipeline-id (get-in runtime [:params :pipeline-id])
                       :include-associated-persons? (boolean include-associated-persons?)}]
     (if (seq export-fields)
       {:fx [[:dispatch [::export-grid-started]]
             [::ajax/send {:operation :search/export-grid
                           :body request-body
                           :on-ok [::export-grid-succeeded {:listing-id listing-id
                                                            :grid-id grid-id}]
                           :on-error [::export-grid-failed]}]]}
       {:fx [[:dispatch [::export-grid-failed {:message "No columns selected for export."}]]]}))))

(rf/reg-event-fx
 ::export-grid-started
 (fn [_ _]
   {::toast/info {:label "Preparing Excel export..."}}))

(rf/reg-event-fx
 ::export-grid-succeeded
 (fn [_ [_ {:keys [grid-id]} resp]]
   (let [body (:body resp)
         attachment-id (:attachment-id body)]
     ;; The endpoint omits :attachment-id and reports :row-count 0 when nothing
     ;; matched, rather than uploading an empty spreadsheet.
     (if attachment-id
       (do
         (fu/open-attachment! attachment-id)
         {:fx [[:dispatch [::grid-event/trigger-query-search grid-id]]]
          ::toast/success (loc/tr :export-ready-download-started)})
       {::toast/info {:label (loc/tr :export-nothing-to-export)}}))))

(defn- export-error-message
  [err]
  (let [friendly-message (get-in err [:client-friendly-error-data :message])
        fallback-message (:message err)]
    (cond
      (and (string? friendly-message) (seq friendly-message)) friendly-message
      (and (string? fallback-message) (seq fallback-message)) fallback-message
      :else "Please try again.")))

(rf/reg-event-fx
 ::export-grid-failed
 (fn [_ [_ err]]
   (let [error-body (or (:response err) err)
         error-message (export-error-message error-body)]
     (logging/error "Unable to export KYC grid" err)
     {::toast/error (str "Excel export failed: " error-message)})))

(rf/reg-event-fx
 ::create-kyc-profile
 (fn [_ _]
   (entity/open-creator! :entity.type/kyc-profile {})
   {}))

(rf/reg-event-fx
 ::bulk-delete-selected
 (fn [{:keys [db]} [_ {:keys [listing-id]}]]
   (let [selection (get-in db (generic-listing-model/selection-path listing-id))
         selected-ids (selection-helpers/action-ids selection)]
     (if (seq selected-ids)
       (do
         (modals/open-modal!
          confirmation/modal-key
          {:body          (selection-helpers/delete-confirmation-body selection)
           :confirm-text  (loc/tr :confirm-delete-selected-button)
           :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
           :on-confirm    (fn []
                            (-> (conn/sendp! :kyc-profile/mass-action
                                             {:action :delete-kyc-profiles
                                              :kyc-profile-ids selected-ids})
                                (p/then (fn [resp]
                                          (rf/dispatch [::generic-event/clear-selection listing-id])
                                          ;; Wait for the delete's off-thread
                                          ;; search reindex before refetching -
                                          ;; see ui.completion.event.
                                          (rf/dispatch
                                           [::completion-event/await
                                            {:completion-id (:completion/id resp)
                                             :on-complete   [::grid-event/trigger-query-search
                                                             (str listing-id "-grid")]}])))
                                (p/catch (fn [err]
                                           (logging/error "Unable to delete selected kyc-profiles" err)))))})
         {})
       {}))))
